import { useCallback, useLayoutEffect, useState } from 'react';

export type ReviewPane = 'documents' | 'page' | 'fields' | 'lines';

/** Saved sizes are preferences, not permission to push another editor off-screen. */
export function reviewPaneWidths(
  width: number,
  preferredSpine: number,
  preferredFields: number,
  viewerDetached: boolean,
  fieldsDetached: boolean,
) {
  const available = width > 0 ? Math.floor(width) : preferredSpine + preferredFields + 420;
  const narrow = !viewerDetached && available < 132 + 420 + (fieldsDetached ? 0 : 270);
  const spineMax = Math.min(360, Math.max(132, available - 420 - (fieldsDetached ? 0 : 270)));
  const spine = viewerDetached ? 0 : Math.min(preferredSpine, spineMax);
  const fieldsMax = Math.min(600, Math.max(270, available - spine - 420));
  const fields = viewerDetached
    ? available
    : fieldsDetached
      ? 0
      : Math.min(preferredFields, fieldsMax);
  return {
    spine,
    fields,
    spineMax,
    fieldsMax,
    narrow,
    columns:
      viewerDetached || narrow
        ? 'minmax(0, 1fr)'
        : `${spine}px minmax(0, 1fr)${fieldsDetached ? '' : ` ${fields}px`}`,
  };
}

export function useReviewPaneLayout(
  spine: number,
  fields: number,
  viewerDetached: boolean,
  fieldsDetached: boolean,
) {
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  const [selectedPane, setSelectedPane] = useState<ReviewPane>('page');
  const ref = useCallback((next: HTMLDivElement | null) => setElement(next), []);
  const revealPane = useCallback(
    (pane: ReviewPane) => {
      if (viewerDetached || (pane === 'fields' && fieldsDetached)) return;
      setSelectedPane(pane);
    },
    [viewerDetached, fieldsDetached],
  );
  useLayoutEffect(() => {
    if (!element) return;
    const measure = () => {
      // Read focus before a resize hides any pane. A zoom/window change must
      // keep the current editor visible, without blurring or remounting it.
      const focused =
        element.ownerDocument.activeElement?.closest<HTMLElement>('[data-review-pane]');
      if (focused && element.contains(focused) && focused.getClientRects().length > 0)
        setSelectedPane(focused.dataset.reviewPane as ReviewPane);
      setWidth(element.clientWidth);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);
  const layout = reviewPaneWidths(width, spine, fields, viewerDetached, fieldsDetached);
  useLayoutEffect(() => {
    if (!element || !layout.narrow) return;
    const focused = element.ownerDocument.activeElement?.closest<HTMLElement>('[data-review-pane]');
    // A thumbnail or "Back to page" button can hide its own pane. Leave a
    // visible keyboard landing; source-to-editor arrival still focuses its
    // exact input in the existing next-frame callback.
    if (focused && element.contains(focused) && focused.getClientRects().length === 0)
      element
        .querySelector<HTMLButtonElement>('.review-pane-switcher button[aria-pressed="true"]')
        ?.focus({ preventScroll: true });
  }, [element, layout.narrow, selectedPane, fieldsDetached, viewerDetached]);
  return {
    ref,
    element,
    revealPane,
    activePane: fieldsDetached && selectedPane === 'fields' ? 'page' : selectedPane,
    ...layout,
  };
}
